// The player's bets that are still riding, one row each: which way, the stake, what it is worth
// now as a multiple, and the button that takes it.

import { useState } from "react";
import type { Instrument, Position } from "../../sdk";
import { useAccount } from "../../state/account";
import { useInstruments } from "../../state/market";
import { useValuation } from "../../state/valuation";
import { big, usd } from "../../lib/num";
import { cashOut, playLabel, usePlays, type Play } from "../../play/book";
import { buzz } from "../../play/hooks";
import { multiple } from "../../play/tape";
import { navigate, tableHref } from "../../router";

/** The live multiple of a position: payout over stake, and the payout itself. */
export function useWorth(position: Position | null) {
  const valued = useValuation(position?.positionId ?? "");
  if (!position || !valued) return null;
  const payout = big(valued.payout);
  const stake = big(position.margin);
  return { payout, stake, multiple: multiple(payout, stake), up: payout >= stake, risk: valued.riskBp ?? 0, liquidated: valued.liquidated };
}

export function CashOut({ position, play, closing, className }: { position: Position; play: Play | undefined; closing: boolean; className?: string }) {
  const worth = useWorth(position);
  const [busy, setBusy] = useState(false);
  const cashing = closing || play?.status === "cashing" || busy;
  const label = play ? playLabel(play) : `${position.isLong ? "Up" : "Down"}`;
  return (
    <button
      type="button"
      className={className ?? "cash"}
      disabled={cashing || worth?.liquidated}
      onClick={async (event) => {
        event.stopPropagation();
        buzz(16);
        setBusy(true);
        try {
          await cashOut(position.positionId, `Cash out · ${label}`);
        } catch {
          // told by a toast
        } finally {
          setBusy(false);
        }
      }}
    >
      {cashing ? <span className="spinner" aria-hidden /> : worth ? <>Cash out <span className="num">{usd(worth.payout)}</span></> : "Cash out"}
    </button>
  );
}

function Ticket({
  position,
  instrument,
  play,
  closing,
  focused,
  onFocus,
}: {
  position: Position;
  instrument: Instrument | undefined;
  play: Play | undefined;
  closing: boolean;
  focused: boolean;
  onFocus: (id: string) => void;
}) {
  const worth = useWorth(position);
  const coin = instrument?.symbol ?? "";
  const side = position.isLong ? "up" : "down";
  return (
    <article
      className={`ticket ticket-${side} ${focused ? "is-focus" : ""} ${worth && worth.risk >= 7000 ? "is-hot" : ""}`}
      onClick={() => {
        onFocus(position.positionId);
        navigate(tableHref(coin), false, true);
      }}
    >
      <div className="ticket-what">
        <span className={`ticket-call ${side}`}>
          {position.isLong ? "▲" : "▼"} {coin}
        </span>
        <span className="ticket-stake num">
          {usd(position.margin)}
          {play?.target ? ` · ${play.target}×` : ""}
        </span>
      </div>
      <span className={`ticket-mult num ${worth ? (worth.up ? "is-up" : "is-down") : ""}`}>{worth ? worth.multiple : "—"}</span>
      <CashOut position={position} play={play} closing={closing} />
    </article>
  );
}

export function Tickets({ focusId, onFocus }: { focusId: string | null; onFocus: (id: string) => void }) {
  const account = useAccount();
  const instruments = useInstruments();
  const plays = usePlays();
  const positions = account?.positions ?? [];
  const closing = new Set((account?.operations ?? []).filter((op) => op.action === "closePositions").flatMap((op) => op.positionIds));
  const sent = plays.filter((play) => play.status === "sent");
  if (!positions.length && !sent.length) return null;
  return (
    <section className="tickets" aria-label="Your live bets">
      {sent.map((play) => (
        <article key={play.intentId} className={`ticket ticket-${play.call} is-sent`}>
          <div className="ticket-what">
            <span className={`ticket-call ${play.call}`}>
              {play.call === "up" ? "▲" : "▼"} {play.coin}
            </span>
            <span className="ticket-stake num">{usd(play.stake)}</span>
          </div>
          <span className="ticket-sending">
            <span className="spinner" aria-hidden /> Placing
          </span>
        </article>
      ))}
      {positions.map((position) => (
        <Ticket
          key={position.positionId}
          position={position}
          instrument={instruments.find((item) => item.id === position.instrumentId)}
          play={plays.find((play) => play.positionId === position.positionId)}
          closing={closing.has(position.positionId)}
          focused={position.positionId === focusId}
          onFocus={onFocus}
        />
      ))}
    </section>
  );
}
